/**
 * Project — blog listing position memory.
 *
 * Problem: pagination lives in the URL (`/blog/page:3`, controllers/blog.php),
 * so returning to "Blog" from the top menu is a fresh navigation to page 1 at
 * scroll 0. The browser's own scroll restoration only covers back/forward, not
 * a menu click, so the reader loses their place in the listing.
 *
 * Two values are stored, keyed to one sessionStorage entry:
 *   y — scroll offset at the moment a note was opened
 *   p — the listing page that scroll offset belongs to
 *
 * Restoring p first and y second matters: scrolling to 4000px on page 1 lands
 * nowhere. The `hop` flag makes the page-restore one-shot, so a stale store
 * pointing at a page that no longer exists cannot loop.
 *
 * Not Alpine: Alpine here is the CSP build (assets/js/codey/alpine.js), which
 * is CORE and takes component names only. This is project behaviour with no
 * reactive state — a listener pair, not a component.
 */
(() => {
  const list = document.getElementById('note-listing')
  if (!list) return

  const KEY  = 'blog:return'
  const TTL  = 30 * 60 * 1000
  const PAGE = /\/page:(\d+)/

  const currentPage = () => Number(location.pathname.match(PAGE)?.[1] ?? 1)

  // Kirby params are order-free segments, so appending page: after any
  // tag: segment rebuilds a URL the router still resolves.
  const pageUrl = (n) => {
    const base = location.pathname.replace(PAGE, '').replace(/\/$/, '')
    return (n > 1 ? `${base}/page:${n}` : base) + location.search
  }

  // Safari in private mode throws on setItem, and any browser can have storage
  // blocked outright. Losing the reader's place is not worth an exception, so
  // every access fails soft: read yields null, write and clear give up.
  const read  = () => { try { return JSON.parse(sessionStorage.getItem(KEY)) } catch { return null } }
  const write = (v) => { try { sessionStorage.setItem(KEY, JSON.stringify(v)) } catch { /* storage unavailable */ } }
  const clear = () => { try { sessionStorage.removeItem(KEY) } catch { /* storage unavailable */ } }

  // Save — only clicks inside the listing. Pagination links sit outside it,
  // so paging forward correctly starts at the top of the new page.
  list.addEventListener('click', (event) => {
    if (!event.target.closest('a')) return
    write({ y: window.scrollY, p: currentPage(), t: Date.now() })
  })

  // Restore — this load is a return to the listing.
  const store = read()
  if (!store || Date.now() - store.t > TTL) return clear()

  if (store.p !== currentPage()) {
    if (store.hop) return clear()
    write({ ...store, hop: true })
    return location.replace(pageUrl(store.p))
  }

  // Motion is a courtesy, never a requirement. A reader who has asked the OS
  // for less of it gets the instant jump; everyone else gets the travel, which
  // reads as "you were here" rather than as a page that loaded oddly.
  //
  // The curve is the browser's — behavior:'smooth' exposes no easing or
  // duration hook. Custom easing would mean animating scrollTop in a rAF loop,
  // which buys a curve and costs the browser's own interruption handling.
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches

  clear()
  requestAnimationFrame(() => {
    window.scrollTo({ top: store.y, behavior: reduced ? 'auto' : 'smooth' })
  })
})()
